import { baseActions, baseMutations } from '../base'
import { apiAxios, parseFilters } from '@/helpers'
import { formatDate } from '@/helpers/dateHelper'
import { getNotificationFilters } from '@/helpers/permissions'
import i18n from '@/i18n/i18n'

const initialState = () => {
  return {
    module: 'notifications',
    model: 'notification',
    list: [],
    receiverList: [],
    menuList: [],
    confirmRoleList: [],
    supportList: [],
    badge: 0,
    menuBadge: 0,
    confirmRoleBadge: 0,
    supportBadge: 0,
    loading: false,
    selected: {
      remittent: '',
      sendDate: '',
      receiver: {},
      type: 'custom',
      title: [],
      text: [],
      attachments: [],
      typeRole: [],
      sendType: [],
    },
    filters: {},
    types: [
      'event',
      'match',
      'confirmedRegister',
      'rejectedRegister',
      'follow',
      'custom',
    ],
    typesRemittent: ['fbase', 'entity', 'team', 'user'],
    typesRemittentNoAdmin: ['entity', 'team', 'user'],
    typesReceiver: [
      'all',
      'entity',
      'team',
      'user',
      'category',
      'entityFollowers',
      'teamFollowers',
      'playerFollowers',
    ],
    typesRole: ['president', 'coordinator', 'trainer', 'player', 'familiar'],
    pagination: {
      page: 1,
      rowsPerPage: 10,
      totalItems: 0,
    },
    menuPagination: {
      page: 1,
      rowsPerPage: 10,
      totalItems: 0,
    },
    confirmRolePagination: {
      page: 1,
      rowsPerPage: 5,
      totalItems: 0,
    },
    supportPagination: {
      page: 1,
      rowsPerPage: 5,
      totalItems: 0,
    },
    receiverPagination: {
      page: 1,
      rowsPerPage: 10,
      totalItems: 0,
    },
    stats: null,
    receiverFilters: {},
  }
}

const state = initialState()

const getters = {
  types(state) {
    return state.types.map((item) => ({
      value: item,
      text: i18n.t(`fields.notifications.type.${item}`),
    }))
  },
  typesRemittentNoAdmin(state) {
    return state.typesRemittentNoAdmin.map((item) => ({
      value: item,
      text: i18n.t(`fields.notifications.remittent.${item}`),
    }))
  },
  typesRemittent(state, getters, rootState) {
    const is = (type) =>
      rootState.auth.roles.some(
        (item) => item.confirm && item.roleId && item.roleId.type === type
      )

    let items = []

    if (is('all')) {
      items = ['fbase', 'entity', 'team', 'user']
    } else if (is('president') || is('coordinator')) {
      items = ['entity', 'team', 'user']
    } else if (is('trainer')) {
      items = ['team', 'user']
    }

    return items.map((item) => ({
      value: item,
      text: i18n.t(`fields.notifications.remittent.${item}`),
    }))
  },
  typesReceiver(state) {
    return state.typesReceiver.map((item) => ({
      value: item,
      text:
        item === 'category'
          ? i18n.tc(`models.category`)
          : i18n.t(`fields.notifications.receiver.${item}`),
    }))
  },
  typesRole(state) {
    return state.typesRole.map((item) => ({
      value: item,
      text: i18n.t(`fields.notifications.role.${item}`),
    }))
  },
  formatedList(state, getters) {
    return state.list.map((item) => {
      let result = {
        ...item,
        sendDate: formatDate(item.sendDate, true),
        createdAt: formatDate(item.createdAt, true),
      }
      const type = getters.types.find((type) => type.value === item.type)
      result.type = type ? type.text : '-'
      return result
    })
  },
  receiverList(state) {
    return state.receiverList
  },
  stats(state) {
    const stats = state.stats
    let push = []
    let email = []
    if (state.stats) {
      push.push({
        name: i18n.t('fields.notifications.read'),
        value: stats.read,
      })
      push.push({
        name: i18n.t('fields.notifications.noRead'),
        value: stats.noRead,
      })
      email.push({
        name: i18n.t('fields.notifications.read'),
        value: stats.readEmail,
      })
      email.push({
        name: i18n.t('fields.notifications.noRead'),
        value: stats.noReadEmail,
      })
    }
    return { push, email }
  },
}

const actions = {
  ...baseActions,
  async fetchReceivers({ state, commit }, id) {
    try {
      const notificationId = id || state.idSelected
      if (!notificationId) return
      let query = {
        page: state.receiverPagination.page,
        limit: state.receiverPagination.rowsPerPage,
      }
      let body = {}

      if (state.receiverFilters) {
        Object.keys(state.receiverFilters).forEach((key) => {
          if (
            state.receiverFilters[key] !== '' &&
            state.receiverFilters[key] !== undefined &&
            state.receiverFilters[key] !== null
          ) {
            body[key] = state.receiverFilters[key]
          }
        })
      }
      if (state.receiverPagination.sortBy) {
        query.sort = {
          [state.receiverPagination.sortBy]: state.receiverPagination.descending
            ? -1
            : 1,
        }
      }
      commit('setLoading', true)
      const { data } = await apiAxios
        .post(`${state.module}/${notificationId}/receivers`, body, {
          params: { ...query },
        })
        .finally(() => commit('setLoading', false))
      if (data.data) {
        let list = data.data.users || []
        commit('setReceiverList', list)
        commit('setReceiverPagination', {
          totalItems: data.paginate ? data.paginate.totalData : 0,
        })
      } else commit('setReceiverList', [])
    } finally {
      commit('setLoading', false)
    }
  },
  async fetchStats({ state, commit }, id) {
    try {
      const notificationId = id || state.idSelected
      if (!notificationId) return

      const { data } = await apiAxios.get(
        `${state.module}/${notificationId}/stats`
      )
      if (data.data) {
        let stats = data.data.stats
        commit('setStats', stats)
      } else commit('setStats')
    } finally {
      commit('setLoading', false)
    }
  },
  async fetchMenuList({ commit, state, rootState }, params = {}) {
    const userId = rootState.auth.user._id
    params.userId = userId
    params.read = false
    params.sendDate = new Date()
    params.typeMode = 'backoffice'

    let query = {
      page: state.menuPagination.page,
      limit: state.menuPagination.rowsPerPage,
    }

    try {
      commit('setLoading', true)
      const { data } = await apiAxios.post(state.module, params, {
        params: query,
      })
      // await dispatch('fetchBadge')
      if (data.data) {
        let list = data.data[state.model] || data.data[state.module] || []
        commit('setMenuList', list)
        commit('setMenuBadge', data.data.badge)
        commit('setMenuPagination', {
          totalItems: data.paginate ? data.paginate.totalData : 0,
        })
      } else {
        commit('setMenuBadge', 0)
        commit('setMenuList', [])
        commit('resetMenuPagination')
      }
    } finally {
      commit('setLoading', false)
    }
  },
  async fetchConfirmRoleList({ commit, state, rootState }, params = {}) {
    const userId = rootState.auth.user._id
    params.userId = userId
    params.read = false
    params.sendDate = new Date()

    let query = {
      page: state.confirmRolePagination.page,
      limit: state.confirmRolePagination.rowsPerPage,
    }

    try {
      commit('setLoading', true)
      const { data } = await apiAxios.post(
        state.module + '/confirmRole',
        params,
        {
          params: query,
        }
      )
      // await dispatch('fetchBadge')
      if (data.data) {
        let list = data.data[state.model] || data.data[state.module] || []
        commit('setConfirmRoleList', list)
        commit('setConfirmRoleBadge', data.data.badge)
        commit('setConfirmRolePagination', {
          totalItems: data.paginate ? data.paginate.totalData : 0,
        })
      } else {
        commit('setConfirmRoleBadge', 0)
        commit('setConfirmRoleList', [])
        commit('resetConfirmRolePagination')
      }
    } finally {
      commit('setLoading', false)
    }
  },
  async fetchSupportList({ commit, state, rootState }, params = {}) {
    const userId = rootState.auth.user._id
    params.userId = userId
    params.read = false
    params.sendDate = new Date()
    params.typeMode = 'backoffice'
    params.type = 'support'

    let query = {
      page: state.supportPagination.page,
      limit: state.supportPagination.rowsPerPage,
    }

    try {
      commit('setLoading', true)
      const { data } = await apiAxios.post(state.module, params, {
        params: query,
      })
      // await dispatch('fetchBadge')
      if (data.data) {
        let list = data.data[state.model] || data.data[state.module] || []
        commit('setSupportList', list)
        commit('setSupportBadge', data.data.badge)
        commit('setSupportPagination', {
          totalItems: data.paginate ? data.paginate.totalData : 0,
        })
      } else {
        commit('setSupportBadge', 0)
        commit('setSupportList', [])
        commit('resetSupportPagination')
      }
    } finally {
      commit('setLoading', false)
    }
  },
  async fetchList({ commit, state }, params = {}) {
    let { body, query } = parseFilters(
      state,
      params,
      getNotificationFilters(params)
    )
    commit('setLoading', true)
    const { data } = await apiAxios
      .post(state.module + '/format', body, { params: { ...query } })
      .finally(() => commit('setLoading', false))

    commit('setPagination', {
      totalItems: data.paginate ? data.paginate.totalData : 0,
    })

    const list = data.data
      ? data.data[state.model] || data.data[state.module]
      : []
    commit('setList', list)
  },
  async read({ state }, id) {
    await apiAxios.post(`${state.module}/read/${id}`)
  },
  async readAll({ state }, params = { type: 'normal' }) {
    await apiAxios.post(`${state.module}/read/all`, {
      type: params.type,
    })
  },

  async fetchBadge({ commit, state, rootState }, params = {}) {
    let userId = rootState.auth.user._id
    const { data } = await apiAxios.get(
      state.module + '/total/badge/' + userId,
      params
    )
    commit('setBadge', data.data.badge)
  },
}

const mutations = {
  ...baseMutations,
  resetState(state) {
    const newState = initialState()
    Object.keys(state).forEach((key) => {
      delete state[key]
    })
    Object.assign(state, newState)
  },
  reset(state) {
    state.selected = initialState().selected
    state.idSelected = ''
  },
  setBadge(state, payload) {
    state.badge = payload || 0
  },
  setMenuBadge(state, payload) {
    state.menuBadge = payload || 0
  },
  setConfirmRoleBadge(state, payload) {
    state.confirmRoleBadge = payload || 0
  },
  setSupportBadge(state, payload) {
    state.supportBadge = payload || 0
  },
  setList(state, payload) {
    state.list = []
    if (Array.isArray(payload)) {
      payload.forEach((item) => state.list.push(item))
    }
  },
  setMenuList(state, payload) {
    state.menuList = []
    if (Array.isArray(payload)) {
      payload.forEach((item) => state.menuList.push(item))
    }
  },
  setConfirmRoleList(state, payload) {
    state.confirmRoleList = []
    if (Array.isArray(payload)) {
      payload.forEach((item) => state.confirmRoleList.push(item))
    }
  },
  setSupportList(state, payload) {
    state.supportList = []
    if (Array.isArray(payload)) {
      payload.forEach((item) => state.supportList.push(item))
    }
  },
  setReceiverList(state, payload) {
    state.receiverList = []
    if (Array.isArray(payload)) {
      payload.forEach((item) => state.receiverList.push(item))
    }
  },
  setReceiverPagination(state, payload) {
    if (payload) {
      state.receiverPagination = Object.assign(
        {},
        state.receiverPagination,
        payload
      )
    }
  },
  setStats(state, payload) {
    state.stats = payload
  },
  resetReceiverPagination(state) {
    state.receiverPagination = {
      hasMore: false,
      page: 1,
      rowsPerPage: 10,
      totalItems: 0,
    }
  },
  setMenuPagination(state, payload) {
    if (payload) {
      state.menuPagination = Object.assign({}, state.menuPagination, payload)
    }
  },
  resetMenuPagination(state) {
    state.menuPagination = {
      hasMore: false,
      page: 1,
      rowsPerPage: 10,
      totalItems: 0,
    }
  },
  setConfirmRolePagination(state, payload) {
    if (payload) {
      state.confirmRolePagination = Object.assign(
        {},
        state.confirmRolePagination,
        payload
      )
    }
  },
  resetConfirmRolePagination(state) {
    state.confirmRolePagination = {
      hasMore: false,
      page: 1,
      rowsPerPage: 10,
      totalItems: 0,
    }
  },
  setSupportPagination(state, payload) {
    if (payload) {
      state.supportPagination = Object.assign(
        {},
        state.supportPagination,
        payload
      )
    }
  },
  resetSupportPagination(state) {
    state.supportPagination = {
      hasMore: false,
      page: 1,
      rowsPerPage: 10,
      totalItems: 0,
    }
  },
}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
}
